import React, { useState, useEffect } from 'react';
import { ExternalLink, Shield, Lock, FileCheck, AlertTriangle } from 'lucide-react';

const topCasinos = [
  {
    rank: 1,
    name: 'Betsson',
    rating: '5.0',
    bonus: '100% op til 500 kr',
    withdrawalTime: '0-24 timer',
    paymentMethods: 'Trustly, MobilePay, Visa, Mastercard',
    license: 'Dansk',
    affiliate: 'https://example.com/betsson'
  },
  {
    rank: 2,
    name: 'LeoVegas',
    rating: '5.0',
    bonus: 'Op til 1.000 kr',
    withdrawalTime: '0-2 timer',
    paymentMethods: 'Trustly, MobilePay, PayPal, Skrill',
    license: 'Dansk',
    affiliate: 'https://example.com/leovegas'
  },
  {
    rank: 3,
    name: 'Casumo',
    rating: '5.0',
    bonus: '100% op til 1.000 kr',
    withdrawalTime: '0-24 timer',
    paymentMethods: 'Trustly, E-wallets, MobilePay',
    license: 'Dansk',
    affiliate: 'https://example.com/casumo'
  },
  {
    rank: 4,
    name: 'Mr Green',
    rating: '4.9',
    bonus: '100% op til 1.000 kr',
    withdrawalTime: '1-24 timer',
    paymentMethods: 'Trustly, E-wallets, Kort',
    license: 'Dansk',
    affiliate: 'https://example.com/mrgreen'
  },
  {
    rank: 5,
    name: 'Betinia',
    rating: '4.8',
    bonus: '100% op til 1.000 kr',
    withdrawalTime: '1-24 timer',
    paymentMethods: 'Trustly, MobilePay, Kort',
    license: 'Dansk',
    affiliate: 'https://example.com/betinia'
  }
];

const navigationSections = [
  { id: 'top', label: 'Top 5' },
  { id: 'what', label: 'Hvad er det?' },
  { id: 'why', label: 'Hvorfor?' },
  { id: 'security', label: 'Sikkerhed' },
  { id: 'tax', label: 'Skat' },
  { id: 'verification', label: 'Verifikation' },
  { id: 'payment', label: 'Betalinger' },
  { id: 'factors', label: 'Faktorer' },
  { id: 'best', label: 'Bedste' }
];

function HomePage() {
  const [activeSection, setActiveSection] = useState('top');

  useEffect(() => {
    const handleScroll = () => {
      const sections = navigationSections.map(section => {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          return {
            id: section.id,
            top: rect.top,
            bottom: rect.bottom
          };
        }
        return null;
      }).filter(Boolean);

      const current = sections.find(section =>
        section.top <= 150 && section.bottom > 150
      );

      if (current) {
        setActiveSection(current.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* SEO Meta Tags would go in <head> */}
      {/* <title>Udenlandsk Casino Med Hurtig Udbetaling 2025 Bedste Guide</title> */}
      {/* <meta name="description" content="Find udenlandsk casino med hurtig udbetaling i Danmark. Sammenlign Trustly, krypto og e-wallets. Få dine gevinster på minutter med vores komplette guide til hurtig udbetaling." /> */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section with Navigation */}
        <section className="py-12 sm:py-16 max-w-4xl mx-auto">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-cyan-500/20 to-blue-600/20 blur-3xl -z-10"></div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
              <span className="bg-gradient-to-r from-blue-300 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
                Udenlandsk Casino Med Hurtig Udbetaling Bedste Guide 2025
              </span>
            </h1>
            <p className="text-xs sm:text-sm text-blue-300 mb-4">Sidst opdateret: 25. November 2025</p>
          </div>

          <p className="text-lg sm:text-xl text-blue-200 mb-4 sm:mb-6 leading-relaxed">
            Casinoer med hurtige udbetalinger er spilleplatforme, der prioriterer en hurtig behandling af gevinster. Hvor traditionelle casinoer kan tage 3-7 hverdage for at udbetale dine penge, kan moderne hurtige casinoer overføre gevinster på nogle få minutter til maksimalt 24 timer.
          </p>

          <p className="text-base sm:text-lg text-blue-300 mb-4 leading-relaxed">
            <a href="https://site.com/casino-med-trustly" className="text-cyan-400 hover:text-cyan-300 underline transition-colors touch-manipulation">Trustly casino</a> har revolutioneret online casinoer med hurtig udbetaling.
          </p>

          <p className="text-base sm:text-lg text-blue-300 mb-6 sm:mb-8 leading-relaxed">
            Lær om betalingsmetoder, verifikation og hvordan du får dine gevinster hurtigt og sikkert. Find de bedste udenlandske casinoer med hurtig udbetaling. Lær om Trustly, krypto, e-wallets og hvordan du får dine gevinster på få minutter. Komplet guide til danske spillere 2025.
          </p>

          {/* Navigation Bar - Sticky */}
          <div className="sticky top-0 z-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-slate-900/98 backdrop-blur-sm border-y border-blue-700/30 shadow-lg shadow-blue-900/20">
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center justify-center space-x-1 max-w-4xl mx-auto">
              {navigationSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-200 ${activeSection === section.id
                      ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white'
                      : 'text-blue-300 hover:text-cyan-300 hover:bg-slate-800/50'
                    }`}
                >
                  {section.label}
                </button>
              ))}
            </div>

            {/* Mobile Navigation - Horizontal Scrollable */}
            <div className="md:hidden">
              <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
                <div className="flex space-x-2 min-w-max pb-1">
                  {navigationSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors duration-200 flex-shrink-0 touch-manipulation ${activeSection === section.id
                          ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white'
                          : 'text-blue-300 bg-slate-800/70 active:bg-slate-800'
                        }`}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ranking Table Section */}
        <section id="top" className="py-12 scroll-mt-20">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold mb-10 text-center">
              <span className="bg-gradient-to-r from-cyan-400 via-blue-300 to-cyan-400 bg-clip-text text-transparent">
                Top 5 Casinoer med Hurtigste Udbetalinger
              </span>
            </h2>

            {/* Mobile Card Layout */}
            <div className="block lg:hidden space-y-4">
              {topCasinos.map((casino) => (
                <div key={casino.rank} className="bg-slate-800/50 rounded-2xl p-5 border border-blue-700/30 shadow-xl">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 via-blue-500 to-blue-600 text-white font-bold text-lg shadow-lg flex-shrink-0">
                      {casino.rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-blue-100 mb-1">{casino.name}</h3>
                      <div className="text-sm font-medium text-amber-400">⭐ {casino.rating}</div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <div className="text-xs text-cyan-400 font-semibold mb-1.5 uppercase tracking-wide">Velkomstbonus</div>
                      <div className="text-base text-blue-200 font-medium">{casino.bonus}</div>
                    </div>

                    <div>
                      <div className="text-xs text-cyan-400 font-semibold mb-1.5 uppercase tracking-wide">Udbetalingstid</div>
                      <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30">
                        <span className="text-base font-bold text-green-300">⚡ {casino.withdrawalTime}</span>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-cyan-400 font-semibold mb-1.5 uppercase tracking-wide">Betalingsmetoder</div>
                      <div className="text-sm text-blue-300 leading-relaxed">{casino.paymentMethods}</div>
                    </div>

                    <div>
                      <div className="text-xs text-cyan-400 font-semibold mb-1.5 uppercase tracking-wide">Licens</div>
                      <span className="inline-flex px-4 py-2 text-sm font-bold rounded-full bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-green-300 border border-green-400/30">
                        {casino.license}
                      </span>
                    </div>
                  </div>

                  <a
                    href={casino.affiliate}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="mt-5 w-full inline-flex items-center justify-center px-6 py-4 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 active:from-cyan-700 active:to-blue-700 text-white text-base font-bold shadow-lg touch-manipulation"
                  >
                    Besøg Casino →
                  </a>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden lg:block">
              <div className="overflow-hidden bg-slate-800/50 rounded-2xl shadow-2xl shadow-blue-900/30 border border-blue-700/30">
                <table className="min-w-full">
                  <thead className="bg-gradient-to-r from-blue-900 to-slate-900 border-b border-blue-700/50">
                    <tr>
                      <th className="px-6 py-5 text-left text-xs font-bold text-cyan-400 uppercase tracking-wider">#</th>
                      <th className="px-6 py-5 text-left text-xs font-bold text-cyan-400 uppercase tracking-wider">Casino</th>
                      <th className="px-6 py-5 text-left text-xs font-bold text-cyan-400 uppercase tracking-wider">Bedømmelse</th>
                      <th className="px-6 py-5 text-left text-xs font-bold text-cyan-400 uppercase tracking-wider">Velkomstbonus</th>
                      <th className="px-6 py-5 text-left text-xs font-bold text-cyan-400 uppercase tracking-wider">Udbetalingstid</th>
                      <th className="px-6 py-5 text-left text-xs font-bold text-cyan-400 uppercase tracking-wider">Betalingsmetoder</th>
                      <th className="px-6 py-5 text-left text-xs font-bold text-cyan-400 uppercase tracking-wider">Licens</th>
                      <th className="px-6 py-5 text-left text-xs font-bold text-cyan-400 uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-800/30">
                    {topCasinos.map((casino) => (
                      <tr key={casino.rank} className="hover:bg-blue-900/30 transition-all duration-300 group">
                        <td className="px-6 py-6 whitespace-nowrap">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 via-blue-500 to-blue-600 text-white font-bold text-sm shadow-lg shadow-blue-500/50 group-hover:shadow-cyan-400/50 group-hover:scale-110 transition-all duration-300">
                            {casino.rank}
                          </div>
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap">
                          <div className="text-lg font-bold text-blue-100 group-hover:text-cyan-300 transition-colors duration-300">{casino.name}</div>
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap">
                          <div className="text-sm font-medium text-amber-400">⭐ {casino.rating}</div>
                        </td>
                        <td className="px-6 py-6">
                          <div className="text-sm text-blue-200">{casino.bonus}</div>
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap">
                          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30">
                            <span className="text-sm font-bold text-green-300">⚡ {casino.withdrawalTime}</span>
                          </div>
                        </td>
                        <td className="px-6 py-6">
                          <div className="text-xs text-blue-300 max-w-[200px]">{casino.paymentMethods}</div>
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap">
                          <span className="inline-flex px-3 py-1.5 text-xs font-bold rounded-full bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-green-300 border border-green-400/30">
                            {casino.license}
                          </span>
                        </td>
                        <td className="px-6 py-6 whitespace-nowrap text-right text-sm">
                          <a
                            href={casino.affiliate}
                            target="_blank"
                            rel="noopener noreferrer sponsored"
                            className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold shadow-lg shadow-blue-500/30 hover:shadow-cyan-400/50 transition-all duration-300 hover:scale-105"
                          >
                            Besøg →
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-center text-xs text-blue-400 mt-6 leading-relaxed">
              18+ | Spil ansvarligt | Selvudelukkelse via ROFUS.nu | Alle casinoer har dansk licens
            </p>
          </div>

          <div className="mt-8 bg-blue-900/20 rounded-xl p-6 border border-blue-500/30">
            <p className="text-blue-200 text-center leading-relaxed">
              <strong className="text-blue-100">Vigtigt:</strong> Alle anbefalede casinoer har gyldige licenser og stærk omdømme. Vælg baseret på dine prioriteter omkring betalingsmetoder, spilpræferencer og bonustilbud. Husk altid at spille ansvarligt og inden for dine økonomiske rammer.
            </p>
          </div>
        </section>

        {/* Main Content Sections */}
        <section className="py-8 sm:py-16 max-w-4xl mx-auto space-y-8 sm:space-y-12">

          {/* Why Speed Matters */}
          <div id="why" className="bg-slate-800/50 rounded-2xl p-6 sm:p-8 md:p-12 border border-blue-700/30 shadow-xl scroll-mt-24">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Hvorfor Hastighed Betyder Noget
            </h2>

            <p className="text-base sm:text-lg text-blue-200 leading-relaxed">
              Udbetalingshastighed handler om mere end bekvemmelighed. Et online casino med hurtig udbetaling giver dig en øjeblikkelig bekræftelse på, at dine gevinster er reelle og eliminerer nervøsiteten ved langtrukne ventetider.
            </p>
          </div>

          {/* Security Section */}
          <div id="security" className="bg-slate-800/50 rounded-2xl p-6 sm:p-8 md:p-12 border border-blue-700/30 shadow-xl scroll-mt-24">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent flex items-center gap-3">
              <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-400 flex-shrink-0" />
              <span>Sikkerhed på Hurtige Casinoer</span>
            </h2>

            <p className="text-base sm:text-lg text-blue-200 mb-6 leading-relaxed">
              Når det kommer til at vælge casinoer med hurtige udbetalinger, så glem ikke sikkerheden. Dine penge og personlige oplysninger skal være beskyttet ordentligt.
            </p>

            <div className="space-y-4 sm:space-y-6">
              <div className="bg-slate-900/50 rounded-xl p-5 sm:p-6 border border-blue-600/20">
                <h3 className="text-xl sm:text-2xl font-bold text-cyan-300 mb-3 sm:mb-4 flex items-center gap-2">
                  <FileCheck className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                  <span>Licenser og Regulering</span>
                </h3>
                <p className="text-sm sm:text-base text-blue-200 mb-3 sm:mb-4 leading-relaxed">
                  Her er det første du bør tjekke, har casinoet en ordentlig licens? Når du spiller på et licenseret udenlandsk casino med hurtig udbetaling, er du sikret både hastighed og sikkerhed
                </p>
                <ul className="space-y-2 text-sm sm:text-base text-blue-200 mb-3 sm:mb-4">
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2 sm:mr-3 mt-1 flex-shrink-0">•</span>
                    <span>Malta Gaming Authority (MGA)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2 sm:mr-3 mt-1 flex-shrink-0">•</span>
                    <span>UK Gambling Commission</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2 sm:mr-3 mt-1 flex-shrink-0">•</span>
                    <span>Curaçao eGaming</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-2 sm:mr-3 mt-1 flex-shrink-0">•</span>
                    <span>Gibraltar Gambling Commissioner</span>
                  </li>
                </ul>
                <p className="text-sm sm:text-base text-blue-200 leading-relaxed">
                  Et licens betyder at casinoet er underlagt kontrol og at krav om fair spil og udbetalinger overholdes.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-5 sm:p-6 border border-blue-600/20">
                <h3 className="text-xl sm:text-2xl font-bold text-cyan-300 mb-3 sm:mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                  <span>SSL-Kryptering og Datasikkerhed</span>
                </h3>
                <p className="text-sm sm:text-base text-blue-200 mb-3 leading-relaxed">
                  SSL-kryptering er den samme teknologi som din netbank anvender. De bedste udenlandske casinoer kører med enten 128-bit eller 256-bit SSL-kryptering. Hvad betyder det? I praksis betyder det, at dine data er krypteret på samme niveau som hos bankerne. Så når du indbetaler eller udbetaler penge, er forbindelsen sikret.
                </p>
                <p className="text-sm sm:text-base text-blue-200 leading-relaxed">
                  Du kan se det på hængelåsen i browserens adresselinje.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-5 sm:p-6 border border-blue-600/20">
                <h3 className="text-xl sm:text-2xl font-bold text-cyan-300 mb-3 sm:mb-4">Adskillelse af Spillemidler</h3>
                <p className="text-sm sm:text-base text-blue-200 leading-relaxed">
                  Seriøse casinoer adskiller spillernes midler fra virksomhedens drift, så dine penge er altid sikre selv hvis casinoet kommer i økonomiske problemer.
                </p>
              </div>
            </div>
          </div>

          {/* Tax Section */}
          <div id="tax" className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-blue-700/30 shadow-xl shadow-blue-900/20 scroll-mt-20">
            <h2 className="text-4xl font-bold mb-8 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Er gevinster skattefri på udenlandsk casino med hurtig udbetaling?
            </h2>

            <p className="text-lg text-blue-200 mb-6 leading-relaxed">
              Hvis du spiller på udenlandske casinoer med hurtig udbetaling, skal du være opmærksom på skattereglerne. De er nemlig anderledes end på danske casinoer.
            </p>

            <div className="space-y-6">
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Gevinster på Danske Casinoer er Skattefrie</h3>
                <p className="text-blue-200 leading-relaxed">
                  Hvis du spiller på et casino med dansk licens er alle dine gevinster 100% skattefrie. Danske licenserede casinoer betaler selv afgift til staten så spilleren ikke skal betale skat.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Gevinster på Udenlandske Casinoer</h3>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  Spiller du på et udenlandsk casino uden dansk licens, så bliver det lidt mere kompliceret med skatten. Skattelovgivningen siger at gevinster fra udenlandske gambling sider i princippet skal indberettes som personlig indkomst.
                </p>
                <h4 className="text-xl font-bold text-cyan-300 mb-3 mt-6">Hvornår Skal Du Betale Skat?</h4>
                <p className="text-blue-200 leading-relaxed mb-4">
                  Skattestyrelsen vurderer selv om din gambling aktivitet betragtes som erhvervsmæssig eller hobbybaseret. Spiller du ofte og vinder jævnligt på udenlandske casinoer, så kan Skattestyrelsen betragte det som erhvervsmæssig virksomhed. Og det betyder at dine gevinster kan blive skattepligtige.
                </p>
                <p className="text-blue-200 leading-relaxed">
                  Hobbyspil er som udgangspunkt skattefrit. Men der er ikke nogen klar grænse for hvornår det skifter fra hobby til erhverv. Det er lidt af en gråzone. Ved tvivl bør du konsultere en skatterådgiver eller kontakte Skattestyrelsen direkte.
                </p>
              </div>
            </div>
          </div>

          {/* Account Verification Section */}
          <div id="verification" className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-blue-700/30 shadow-xl shadow-blue-900/20 scroll-mt-20">
            <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Kontoverifikation på Udenlandsk Casino med Hurtig Udbetaling
            </h2>

            <p className="text-lg text-blue-200 mb-6 leading-relaxed">
              Inden du kan hæve dine første gevinster fra et online casino, skal du igennem noget, der hedder KYC-verifikation. Det står for "Know Your Customer" og ja, det er lovpligtigt. Alle seriøse casinoer skal gøre det.
            </p>

            <p className="text-lg text-blue-200 mb-6 leading-relaxed">
              Hvorfor så besværligt? Dels handler det om at beskytte dig, dels skal casinoerne overholde hvidvaskningsloven.
            </p>

            <div className="space-y-6">
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Hvad er KYC?</h3>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  KYC er et sikkerhedscheck der løser flere problemer på én gang.
                </p>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  Casinoet skal vide, at du rent faktisk er den, du siger du er og at du er gammel nok til at spille. Det kan være 18+ eller nogle steder 21+.
                </p>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  Her kommer loven ind i billedet. Casinoerne er tvunget til at holde styr på, hvem der flytter penge ind og ud. Det handler om at stoppe kriminelle i at bruge casinoer til at hvidvaske penge.
                </p>
                <p className="text-blue-200 leading-relaxed">
                  Processen hjælper faktisk også dig. Hvis nogen forsøger at kapre din konto eller stjæle din identitet, er der en sikkerhedsspærre der gør det sværere at kapre.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Påkrævede Dokumenter</h3>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  For at gennemføre verifikationen på casinoer med hurtige udbetalinger skal du typisk indsende følgende dokumenter:
                </p>
                <ul className="space-y-3 text-blue-200">
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">•</span>
                    <span><strong className="text-blue-100">Billedlegitimation:</strong> Pas, kørekort eller ID-kort med tydeligt foto og personlige oplysninger</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">•</span>
                    <span><strong className="text-blue-100">Adressebevis:</strong> Forbrugsregning, kontoudtog eller offentligt dokument max 3 måneder gammelt</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">•</span>
                    <span><strong className="text-blue-100">Betalingsbevis:</strong> Screenshot af kreditkort eller e-wallet med skjulte cifre (kun første 6 og sidste 4 cifre synlige)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Verifikationstid</h3>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  Verifikationsprocessen tager normalt mellem 24-72 timer på de fleste udenlandske casinoer med hurtig udbetaling.
                </p>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  Nogle casinoer tilbyder dog ekspres verifikation, hvor processen kan være færdig lynhurtigt, ofte kun få timer.
                </p>
                <p className="text-blue-200 leading-relaxed">
                  Det er vigtigt at indsende klare og læselige dokumenter for at undgå forsinkelser.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Tips til Hurtig Verifikation</h3>
                <ul className="space-y-3 text-blue-200">
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">•</span>
                    <span>Prøv at verificer din konto straks efter registrering, inden du begynder at spille</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">•</span>
                    <span>Sørg for at alle dokumenter er i højeste kvalitet, synlige og læselige</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">•</span>
                    <span>Upload dokumenter i de korrekte formater normalt JPG, PNG eller PDF</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">•</span>
                    <span>Kontakt deres kundeservice hvis du er i tvivl om hvilke dokumenter de kræver</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-400 mr-3 mt-1">•</span>
                    <span>Hold dine dokumenter klar før du registrer så du kan spare tid</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Pay N Play Casinoer</h3>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  Pay N Play-casinoer er hvis du vil have det hurtigt overstået. De bruger BankID til at verificere dig direkte gennem din bank. Så du kan indbetale og få pengene ud igen på bare nogle få minutter og det kan gøres uden at skulle oprette en konto først.
                </p>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  Det er den hurtigste måde at komme i gang på hvis du ikke gider al den registrering og venten på godkendelse.
                </p>
                <h4 className="text-xl font-bold text-cyan-300 mb-3">Sikkerhed ved Pay N Play</h4>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  Men er det sikkert? Ja, faktisk er det ret smart sikkerhedsmæssigt. Når du bruger BankID, trækker casinoet dine oplysninger direkte fra din bank som allerede har verificeret dig grundigt. Din bank kender dig jo og har styr på, hvem du er.
                </p>
                <p className="text-blue-200 mb-4 leading-relaxed">
                  Fordelen er, at du slipper for at uploade pas og dokumenter, som skal ligge på casinoets servere. Færre steder med dine personlige data betyder færre steder, hvor de kan blive lækket.
                </p>
                <p className="text-blue-200 leading-relaxed">
                  Ulempen? Du skal stole på, at casinoet håndterer forbindelsen til din bank ordentligt. Så tjek altid at Pay N Play-casinoet har en solid licens (MGA, UKGC eller lignende) før du bruger det.
                </p>
              </div>
            </div>
          </div>

          {/* Payment Methods Section */}
          <div id="payment" className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-blue-700/30 shadow-xl shadow-blue-900/20 scroll-mt-20">
            <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Hurtigste Betalingsmetoder til Øjeblikkelige Udbetalinger
            </h2>

            <p className="text-lg text-blue-200 mb-6 leading-relaxed">
              Hvilken betalingsmetode du vælger gør faktisk en kæmpe forskel på casinoer med hurtige udbetalinger. Forskellen kan være alt fra få minutter til flere dage, så det er værd at vide, hvad der er hurtigst.
            </p>

            <p className="text-lg text-blue-200 mb-6 leading-relaxed">
              Her er de bedste muligheder for danske spillere:
            </p>

            <div className="space-y-6">
              {/* 1. Trustly */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">
                  Trustly og Instant Banking
                </h3>
                <div className="mb-3">
                  <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30">
                    <span className="text-sm font-bold text-green-300">⚡ Udbetalingstid: 0-15 minutter</span>
                  </span>
                </div>
                <p className="text-blue-200 leading-relaxed">
                  Trustly er klart den mest populære løsning blandt danske spillere på casinoer med hurtig udbetaling. Tjenesten laver en direkte forbindelse mellem casinoet og din bank, så pengene kan flyttes med det samme. Trustly casinoer kan behandle udbetalinger på under 15 minutter.
                </p>
              </div>

              {/* 2. Crypto */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Kryptovalutaer (Bitcoin, Ethereum, Litecoin)</h3>
                <div className="mb-3">
                  <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30">
                    <span className="text-sm font-bold text-green-300">⚡ Udbetalingstid: 15 minutter - 1 time</span>
                  </span>
                </div>
                <p className="text-blue-200 leading-relaxed">
                  Bitcoin og Ethereum tilbyder næsten øjeblikkelige transaktioner uden mellemmænd. Mange moderne casinoer prioriterer crypto-transaktioner. Bemærk at du skal have en digital wallet for at bruge denne metode.
                </p>
              </div>

              {/* 3. E-wallets */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">E-wallets (Skrill, Neteller, MuchBetter)</h3>
                <div className="mb-3">
                  <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30">
                    <span className="text-sm font-bold text-green-300">⚡ Udbetalingstid: 0-24 timer</span>
                  </span>
                </div>
                <p className="text-blue-200 leading-relaxed">
                  E-wallets er blandt de absolut hurtigste metoder på udenlandske casinoer med hurtig udbetaling. Du modtager typisk pengene på din e-wallet inden for få timer. Skrill og Neteller accepteres bredt på internationale platforme.
                </p>
              </div>

              {/* 4. MobilePay */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">MobilePay</h3>
                <div className="mb-3">
                  <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30">
                    <span className="text-sm font-bold text-green-300">⚡ Udbetalingstid: 0-24 timer</span>
                  </span>
                </div>
                <p className="text-blue-200 leading-relaxed">
                  MobilePay er populær i Danmark, men understøttelsen på internationale spillesider med hurtige udbetalinger er begrænset. Når tilgængeligt, kan udbetalinger ske inden for samme dag.
                </p>
              </div>

              {/* 5. Cards */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Kredit- og Debetkort (Visa, Mastercard)</h3>
                <div className="mb-3">
                  <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-400/30">
                    <span className="text-sm font-bold text-yellow-300">⏱ Udbetalingstid: 1-5 hverdage</span>
                  </span>
                </div>
                <p className="text-blue-200 leading-relaxed">
                  Visa og Mastercard-udbetalinger tager typisk 3-5 hverdage på de fleste udenlandske casinoer. Selvom de ikke er de hurtigste, er de velkendte og pålidelige.
                </p>
              </div>

              {/* 6. Bank Transfer */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Bankoverførsel</h3>
                <div className="mb-3">
                  <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-400/30">
                    <span className="text-sm font-bold text-red-300">⏳ Udbetalingstid: 3-5 hverdage</span>
                  </span>
                </div>
                <p className="text-blue-200 leading-relaxed">
                  Bankoverførsel er den langsomste metode på spillesider. Denne metode er dog sikker og velegnet til større beløb.
                </p>
              </div>
            </div>

            <div className="mt-8 bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
              <h4 className="text-xl font-bold text-cyan-300 mb-3">Sammenligning af metoder</h4>
              <p className="text-blue-200 leading-relaxed">
                For den absolut hurtigste oplevelse på et udenlandsk casino med hurtige gevinster anbefales Trustly som førsteprioriteten. E-wallets og kryptovalutaer er også ekstremt hurtige. Undgå bankoverførsler og traditionelle kreditkort, hvis hastighed er din prioritet.
              </p>
            </div>
          </div>

          {/* Factors Affecting Speed Section */}
          <div id="factors" className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-blue-700/30 shadow-xl shadow-blue-900/20 scroll-mt-20">
            <h2 className="text-4xl font-bold mb-8 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Faktorer der Påvirker Udbetalingshastighed
            </h2>

            <p className="text-lg text-blue-200 mb-6 leading-relaxed">
              Selv når du vælger den hurtigste betalingsmetode på et casino med hurtige udbetalinger, er der flere faktorer, der kan påvirke den faktiske udbetalingstid.
            </p>

            <div className="space-y-6">
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Valg af Betalingsmetode</h3>
                <p className="text-blue-200 leading-relaxed">
                  E-wallets, Trustly og cryptocurrency tilbyder de hurtigste udbetalinger på casino med hurtig udbetaling platforme, mens bankoverførsler og kreditkort kan tage flere dage.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Casinoets Behandlingstid</h3>
                <p className="text-blue-200 leading-relaxed">
                  Hvert udenlandsk casino med hurtig udbetaling har sin egen "pending time". De bedste spillesider har en pending time på 0-2 timer, mens langsommere platforme kan tage op til 48 timer. Søg efter casinoer med "instant withdrawal" for den hurtigste service.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Verifikationsstatus</h3>
                <p className="text-blue-200 leading-relaxed">
                  Uverificerede konti oplever altid forsinkelser på selv de hurtigste casinoer med udbetalinger, da casinoet skal gennemføre KYC-processen først. Verificer din konto med det samme efter registrering for at undgå forsinkelser.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Kvalitet af Kundeservice</h3>
                <p className="text-blue-200 leading-relaxed">
                  Spillesider der tilbyder 24/7 live chat support på dansk, kan løse problemer øjeblikkeligt. De bedste udenlandske casinoer har dedikeret support-personale til betalingsspørgsmål.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Weekender og Helligdage</h3>
                <p className="text-blue-200 leading-relaxed">
                  De mest avancerede udenlandske casinoer har automatiserede systemer, der behandler udbetalinger hele ugen. Hvis du bruger Trustly eller e-wallets, vil weekender typisk ikke påvirke din udbetaling.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Udbetalingsbeløbets Størrelse</h3>
                <p className="text-blue-200 leading-relaxed">
                  Store gevinster kan udløse ekstra sikkerhedskontroller på spillesider. Udbetalinger under 2.000 kr behandles ofte automatisk, mens beløb over 10.000 kr kan kræve manuel gennemgang.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-4">Tidligere Udbetalingshistorik</h3>
                <p className="text-blue-200 leading-relaxed">
                  Etablerede spillere med god historik på et casino med hurtige udbetalinger får ofte hurtigere behandling. Betroede spillere kan få prioriteret service. Dette er en grund til at holde sig til få pålidelige udenlandske casinoer med hurtig udbetaling.
                </p>
              </div>
            </div>
          </div>

          {/* Best Casinos Section */}
          <div id="best" className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-blue-700/30 shadow-xl shadow-blue-900/20 scroll-mt-20">
            <h2 className="text-4xl font-bold mb-8 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Bedste Udenlandske Casinoer med Hurtig Udbetaling
            </h2>

            <p className="text-lg text-blue-200 mb-8 leading-relaxed">
              Her er vores anbefalinger af pålidelige udenlandske casinoer, der leverer hurtige udbetalinger til danske spillere:
            </p>

            <div className="space-y-8">
              {/* Betsson */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-2">1. Betsson Casino</h3>
                <div className="flex flex-wrap gap-4 mb-4">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30">
                    <span className="text-sm font-bold text-green-300">⚡ Udbetalingstid: 0-24 timer</span>
                  </span>
                  <span className="inline-flex px-3 py-1.5 text-sm font-bold rounded-full bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-green-300 border border-green-400/30">
                    Licens: Malta Gaming Authority
                  </span>
                </div>
                <div className="mb-3">
                  <h4 className="text-lg font-bold text-blue-100 mb-2">Fordele:</h4>
                  <ul className="space-y-2 text-blue-200 text-sm">
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Trustly-integration med øjeblikkelige udbetalinger</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Dansk kundeservice døgnet rundt</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Over 20 års erfaring i branchen</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Bredt udvalg af betalingsmetoder</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-blue-100 mb-2">Ulemper:</h4>
                  <ul className="space-y-2 text-blue-200 text-sm">
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2 mt-1">•</span>
                      <span>Kræver fuld KYC-verifikation ved første udbetaling</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2 mt-1">•</span>
                      <span>Visse bonusser har høje omsætningskrav</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* LeoVegas */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-2">2. LeoVegas</h3>
                <div className="flex flex-wrap gap-4 mb-4">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30">
                    <span className="text-sm font-bold text-green-300">⚡ Udbetalingstid: 0-2 timer</span>
                  </span>
                  <span className="inline-flex px-3 py-1.5 text-sm font-bold rounded-full bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-green-300 border border-green-400/30">
                    Licens: Malta Gaming Authority
                  </span>
                </div>
                <div className="mb-3">
                  <h4 className="text-lg font-bold text-blue-100 mb-2">Fordele:</h4>
                  <ul className="space-y-2 text-blue-200 text-sm">
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>"King of Mobile Casino" med fremragende app</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Ekspresverifikation for stamkunder</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Understøtter MobilePay og Trustly</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Omfattende live casino udvalg</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-blue-100 mb-2">Ulemper:</h4>
                  <ul className="space-y-2 text-blue-200 text-sm">
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2 mt-1">•</span>
                      <span>Begrænsede cryptocurrency-muligheder</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2 mt-1">•</span>
                      <span>Udbetalingsgrænser for nye spillere</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Casumo */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-2xl font-bold text-cyan-300 mb-2">3. Casumo</h3>
                <div className="flex flex-wrap gap-4 mb-4">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30">
                    <span className="text-sm font-bold text-green-300">⚡ Udbetalingstid: 0-24 timer</span>
                  </span>
                  <span className="inline-flex px-3 py-1.5 text-sm font-bold rounded-full bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-green-300 border border-green-400/30">
                    Licens: Malta Gaming Authority
                  </span>
                </div>
                <div className="mb-3">
                  <h4 className="text-lg font-bold text-blue-100 mb-2">Fordele:</h4>
                  <ul className="space-y-2 text-blue-200 text-sm">
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Gamification-elementer gør oplevelsen sjov</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Hurtig Trustly og e-wallet support</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Gennemsigtige vilkår og betingelser</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-2 mt-1">•</span>
                      <span>Stærkt fokus på ansvarligt spil</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-blue-100 mb-2">Ulemper:</h4>
                  <ul className="space-y-2 text-blue-200 text-sm">
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2 mt-1">•</span>
                      <span>Ikke alle spil tæller lige meget til bonusomsætning</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-400 mr-2 mt-1">•</span>
                      <span>Mindre udvalg af live dealer spil</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Conclusion Section */}
          <div className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-blue-700/30 shadow-xl shadow-blue-900/20">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Konklusion
            </h2>

            <p className="text-lg text-blue-200 leading-relaxed">
              At vælge det rette udenlandske casino med hurtig udbetaling kræver mere end blot at kigge på hastigheden. Sørg altid for at tjekke casinoets licens og at vælge betalingsmetoder som Trustly eller e-wallets for de hurtigste udbetalinger og verificer din konto så hurtigt som muligt efter registrering. Med denne guide har du nu alle værktøjerne til at finde casinoer med hurtige udbetalinger der matcher dine behov. Husk at spille ansvarligt og kun hos licenserede udbydere for bedste service.
            </p>
          </div>

          {/* FAQ Section */}
          <div className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-blue-700/30 shadow-xl shadow-blue-900/20">
            <h2 className="text-4xl font-bold mb-8 bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              FAQ – Casinoer med hurtige udbetalinger
            </h2>

            <div className="space-y-6">
              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-xl font-bold text-cyan-300 mb-3">Hvad er det hurtigste betalingsmetode på et online casino?</h3>
                <p className="text-blue-200 leading-relaxed">
                  Trustly (0–15 minutter) og e-wallets er de hurtigste muligheder.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-xl font-bold text-cyan-300 mb-3">Kan jeg få udbetaling uden KYC?</h3>
                <p className="text-blue-200 leading-relaxed">
                  Kun på Pay N Play casinoer – ellers nej.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-xl font-bold text-cyan-300 mb-3">Er udenlandske casinoer sikre?</h3>
                <p className="text-blue-200 leading-relaxed">
                  Ja, hvis de har licens fra MGA, UKGC eller Gibraltar.
                </p>
              </div>

              <div className="bg-slate-900/50 rounded-xl p-6 border border-blue-600/20">
                <h3 className="text-xl font-bold text-cyan-300 mb-3">Skal jeg betale skat af gevinster?</h3>
                <p className="text-blue-200 leading-relaxed">
                  Kun hvis casinoet ikke har dansk licens.
                </p>
              </div>
            </div>
          </div>

        </section>

        {/* Responsible Gaming Footer */}
        <section className="py-12 max-w-4xl mx-auto">
          <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6">
            <a
              href="https://ludomani.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 text-red-200 text-center font-semibold text-lg mb-2 hover:text-red-100 transition-colors underline underline-offset-4 w-full"
            >
              ⚠️ Spil Ansvarligt
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="text-red-200/80 text-center text-sm leading-relaxed">
              Gambling kan være vanedannende. Sæt altid grænser for dit spil. Hvis du oplever problemer, kan du søge hjælp hos{' '}
              <a
                href="https://www.spillemyndigheden.dk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-100 hover:text-white underline underline-offset-2 font-medium transition-colors"
              >
                www.spillemyndigheden.dk
              </a>
              {' '}eller kontakte Ludomani-linjen på{' '}
              <a
                href="tel:+4570111810"
                className="text-red-100 hover:text-white underline underline-offset-2 font-medium transition-colors"
              >
                +45 70 11 18 10
              </a>
              . Benyt selvudelukkelsessystemet{' '}
              <a
                href="https://www.rofus.nu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-100 hover:text-white underline underline-offset-2 font-medium transition-colors"
              >
                ROFUS.nu
              </a>
              {' '}hvis nødvendigt.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default HomePage;